import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { matchAPI } from '../lib/api';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Skeleton } from '../components/ui/skeleton';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Calendar, 
  MapPin, 
  Users, 
  Clock,
  ChevronLeft,
  Trophy,
  Loader2,
  Check,
  X,
  UserPlus,
  Target,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { formatCurrency, formatDate, formatTime, getMatchStatusBadge, generateInitials, getEloRank } from '../lib/utils';

export default function MatchDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [processing, setProcessing] = useState(null);
  const [showResultDialog, setShowResultDialog] = useState(false);
  const [resultScores, setResultScores] = useState({ home: 0, away: 0 });
  const [submittingResult, setSubmittingResult] = useState(false);

  const fetchMatch = async () => {
    try {
      const response = await matchAPI.getById(id);
      setMatch(response.data);
    } catch (error) {
      toast.error('Spiel nicht gefunden');
      navigate('/matches');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMatch();
  }, [id]);

  const isHost = match?.host_id === user?.id;
  const isParticipant = match?.participants?.some(p => p.user_id === user?.id);
  const hasApplied = match?.applications?.some(a => a.user_id === user?.id);
  const canApply = !isHost && !isParticipant && !hasApplied && match?.status === 'open';
  
  // Result submission logic
  const canSubmitResult = isHost && 
    (match?.status === 'full' || match?.status === 'awaiting_result') && 
    match?.result_status !== 'submitted' && 
    match?.result_status !== 'confirmed';
  const resultSubmitted = match?.result_status === 'submitted';
  const resultConfirmed = match?.result_status === 'confirmed';

  const handleSubmitResult = async () => {
    setSubmittingResult(true);
    try {
      await matchAPI.submitResult(id, resultScores.home, resultScores.away);
      toast.success('Ergebnis eingereicht! Wartet auf Admin-Bestätigung.');
      setShowResultDialog(false);
      await fetchMatch();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Einreichen');
    } finally {
      setSubmittingResult(false);
    }
  };

  const handleApply = async () => {
    if (user?.wallet_balance < match?.price_per_player) {
      toast.error('Nicht genug Guthaben. Bitte lade dein Wallet auf.');
      navigate('/wallet');
      return;
    }

    setApplying(true);
    try {
      await matchAPI.apply(id);
      toast.success('Bewerbung gesendet!');
      await fetchMatch();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bewerbung fehlgeschlagen');
    } finally {
      setApplying(false);
    }
  };

  const handleAccept = async (userId) => {
    setProcessing(userId);
    try {
      await matchAPI.acceptApplication(id, userId);
      toast.success('Spieler akzeptiert!');
      await fetchMatch();
      await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Akzeptieren');
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (userId) => {
    setProcessing(userId);
    try {
      await matchAPI.rejectApplication(id, userId);
      toast.success('Bewerbung abgelehnt');
      await fetchMatch();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Ablehnen');
    } finally {
      setProcessing(null);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Skeleton className="h-8 w-64 mb-8" />
          <Skeleton className="h-64 rounded-2xl mb-4" />
          <Skeleton className="h-48 rounded-2xl" />
        </div>
      </Layout>
    );
  }

  if (!match) return null;

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="match-details-page">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="shrink-0"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h1 className="font-display font-bold text-xl sm:text-2xl text-tunis-blue">
                {match.field_name}
              </h1>
              <Badge className={getMatchStatusBadge(match.status).className}>
                {getMatchStatusBadge(match.status).text}
              </Badge>
            </div>
            <p className="text-muted-foreground text-sm">Host: {match.host_name}</p>
          </div>
        </div>

        {/* Match Info Card */}
        <Card className="border-slate-200 mb-6" data-testid="match-info-card">
          <CardContent className="p-6">
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-tunis-blue/10 rounded-xl flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-tunis-blue" />
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Datum</div>
                    <div className="font-medium">{formatDate(match.date)}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-tunis-blue/10 rounded-xl flex items-center justify-center">
                    <Clock className="w-5 h-5 text-tunis-blue" />
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Uhrzeit</div>
                    <div className="font-medium">{formatTime(match.time)} ({match.duration_hours}h)</div>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-tunis-blue/10 rounded-xl flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-tunis-blue" />
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Ort</div>
                    <div className="font-medium">{match.field_location}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-tunis-teal/10 rounded-xl flex items-center justify-center">
                    <Users className="w-5 h-5 text-tunis-teal" />
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Spieler</div>
                    <div className="font-medium">{match.current_players}/{match.max_players}</div>
                  </div>
                </div>
              </div>
            </div>

            {match.description && (
              <div className="mt-6 pt-6 border-t border-slate-200">
                <div className="text-sm text-muted-foreground mb-2">Beschreibung</div>
                <p className="text-tunis-blue">{match.description}</p>
              </div>
            )}

            {/* Price & Apply Button */}
            <div className="mt-6 pt-6 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div>
                <div className="text-sm text-muted-foreground">Preis pro Spieler</div>
                <div className="font-display font-bold text-2xl text-tunis-teal">
                  {formatCurrency(match.price_per_player)}
                </div>
              </div>
              
              {canApply && (
                <Button
                  className="btn-primary w-full sm:w-auto"
                  onClick={handleApply}
                  disabled={applying}
                  data-testid="btn-apply"
                >
                  {applying ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <UserPlus className="w-5 h-5 mr-2" />
                      Bewerben
                    </>
                  )}
                </Button>
              )}
              
              {hasApplied && (
                <Badge className="bg-amber-500/10 text-amber-600 px-4 py-2">
                  Bewerbung ausstehend
                </Badge>
              )}
              
              {isParticipant && !isHost && (
                <Badge className="bg-tunis-teal/10 text-tunis-teal px-4 py-2">
                  Du nimmst teil
                </Badge>
              )}
              
              {isHost && (
                <Badge className="bg-tunis-blue/10 text-tunis-blue px-4 py-2">
                  Du bist der Host
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Participants */}
        <Card className="border-slate-200 mb-6" data-testid="participants-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-tunis-blue">
              <Users className="w-5 h-5" />
              Teilnehmer ({match.participants?.length || 0})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {match.participants?.map((participant, index) => {
                const eloRank = getEloRank(participant.elo_points);
                const isHostParticipant = participant.user_id === match.host_id;
                return (
                  <div
                    key={participant.user_id}
                    className="flex items-center gap-4 p-3 bg-slate-50 rounded-xl"
                    data-testid={`participant-${participant.user_id}`}
                  >
                    <Avatar className="h-10 w-10 border-2 border-white shadow">
                      <AvatarFallback className="bg-tunis-blue text-white">
                        {generateInitials(participant.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-tunis-blue">{participant.name}</span>
                        {isHostParticipant && (
                          <Badge variant="outline" className="text-xs">Host</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Trophy className="w-3 h-3 text-amber-500" />
                        <span>{participant.elo_points} ELO</span>
                        <Badge className={`${eloRank.bg} ${eloRank.color} text-xs`}>
                          {eloRank.name}
                        </Badge>
                      </div>
                    </div>
                  </div>
                );
              })}
              
              {/* Empty slots */}
              {Array.from({ length: match.max_players - (match.participants?.length || 0) }).map((_, i) => (
                <div
                  key={`empty-${i}`}
                  className="flex items-center gap-4 p-3 border-2 border-dashed border-slate-200 rounded-xl"
                >
                  <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                    <Users className="w-5 h-5 text-slate-400" />
                  </div>
                  <span className="text-muted-foreground">Freier Platz</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Applications (Host only) */}
        {isHost && match.applications?.length > 0 && (
          <Card className="border-slate-200 mb-6" data-testid="applications-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-tunis-blue">
                <UserPlus className="w-5 h-5" />
                Bewerbungen ({match.applications.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {match.applications.map((application) => {
                  const eloRank = getEloRank(application.elo_points);
                  return (
                    <div
                      key={application.user_id}
                      className="flex items-center gap-4 p-4 bg-amber-50 border border-amber-200 rounded-xl"
                      data-testid={`application-${application.user_id}`}
                    >
                      <Avatar className="h-12 w-12 border-2 border-white shadow">
                        <AvatarFallback className="bg-tunis-blue text-white">
                          {generateInitials(application.name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="font-medium text-tunis-blue">{application.name}</div>
                        <div className="flex items-center gap-2 text-sm">
                          <Trophy className="w-3 h-3 text-amber-500" />
                          <span>{application.elo_points} ELO</span>
                          <Badge className={`${eloRank.bg} ${eloRank.color} text-xs`}>
                            {eloRank.name}
                          </Badge>
                        </div>
                        {application.message && (
                          <p className="text-sm text-muted-foreground mt-1">{application.message}</p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          className="bg-tunis-teal hover:bg-tunis-teal/90"
                          onClick={() => handleAccept(application.user_id)}
                          disabled={processing === application.user_id}
                          data-testid={`btn-accept-${application.user_id}`}
                        >
                          {processing === application.user_id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Check className="w-4 h-4" />
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 border-red-200 hover:bg-red-50"
                          onClick={() => handleReject(application.user_id)}
                          disabled={processing === application.user_id}
                          data-testid={`btn-reject-${application.user_id}`}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Match Result Section */}
        {(resultConfirmed || resultSubmitted || canSubmitResult) && (
          <Card className={`border-slate-200 mb-6 ${resultConfirmed ? 'border-green-300 bg-green-50/30' : resultSubmitted ? 'border-amber-300 bg-amber-50/30' : ''}`} data-testid="result-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-tunis-blue">
                <Target className="w-5 h-5" />
                Spielergebnis
                {resultConfirmed && (
                  <Badge className="bg-green-100 text-green-700 ml-2">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Bestätigt
                  </Badge>
                )}
                {resultSubmitted && (
                  <Badge className="bg-amber-100 text-amber-700 ml-2">
                    <Clock className="w-3 h-3 mr-1" />
                    Wartet auf Bestätigung
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Show Result if submitted or confirmed */}
              {(resultSubmitted || resultConfirmed) && (
                <div className="flex items-center justify-center gap-8 py-6">
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground mb-2">Heim</p>
                    <span className="text-5xl font-bold text-tunis-blue">{match.home_score}</span>
                  </div>
                  <span className="text-3xl font-bold text-slate-400">:</span>
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground mb-2">Auswärts</p>
                    <span className="text-5xl font-bold text-tunis-blue">{match.away_score}</span>
                  </div>
                </div>
              )}
              
              {/* Result Status Message */}
              {resultSubmitted && (
                <div className="flex items-center justify-center gap-2 text-amber-700 bg-amber-100 rounded-lg p-3 mt-4">
                  <AlertCircle className="w-4 h-4" />
                  <span className="text-sm">Das Ergebnis wird vom Admin geprüft</span>
                </div>
              )}
              
              {resultConfirmed && (
                <div className="flex items-center justify-center gap-2 text-green-700 bg-green-100 rounded-lg p-3 mt-4">
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-sm">Ergebnis bestätigt • ELO-Punkte wurden aktualisiert</span>
                </div>
              )}

              {/* Submit Result Button (Host only) */}
              {canSubmitResult && (
                <div className="text-center py-4">
                  <p className="text-muted-foreground mb-4">Das Spiel ist vorbei? Trage das Ergebnis ein!</p>
                  <Button 
                    className="btn-primary"
                    onClick={() => setShowResultDialog(true)}
                  >
                    <Target className="w-4 h-4 mr-2" />
                    Ergebnis eingeben
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Submit Result Dialog */}
        <Dialog open={showResultDialog} onOpenChange={setShowResultDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Spielergebnis eingeben</DialogTitle>
              <DialogDescription>
                Trage das Endergebnis ein. Nach der Eingabe muss ein Admin das Ergebnis bestätigen.
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-6">
              <div className="flex items-center justify-center gap-8">
                <div className="text-center">
                  <Label htmlFor="home-score" className="text-sm text-muted-foreground">Heim (Dein Team)</Label>
                  <Input
                    id="home-score"
                    type="number"
                    min="0"
                    max="99"
                    value={resultScores.home}
                    onChange={(e) => setResultScores({ ...resultScores, home: parseInt(e.target.value) || 0 })}
                    className="w-24 text-center text-3xl font-bold mt-2 h-16"
                  />
                </div>
                <span className="text-3xl font-bold text-slate-400 mt-6">:</span>
                <div className="text-center">
                  <Label htmlFor="away-score" className="text-sm text-muted-foreground">Auswärts (Gegner)</Label>
                  <Input
                    id="away-score"
                    type="number"
                    min="0"
                    max="99"
                    value={resultScores.away}
                    onChange={(e) => setResultScores({ ...resultScores, away: parseInt(e.target.value) || 0 })}
                    className="w-24 text-center text-3xl font-bold mt-2 h-16"
                  />
                </div>
              </div>
              
              <p className="text-center text-sm text-muted-foreground mt-6">
                {resultScores.home > resultScores.away && '🏆 Dein Team hat gewonnen!'}
                {resultScores.home < resultScores.away && '😔 Dein Team hat verloren'}
                {resultScores.home === resultScores.away && '🤝 Unentschieden'}
              </p>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowResultDialog(false)}>
                Abbrechen
              </Button>
              <Button 
                className="btn-primary"
                onClick={handleSubmitResult}
                disabled={submittingResult}
              >
                {submittingResult ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                Ergebnis einreichen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
